import { yupResolver } from '@hookform/resolvers/yup';
import { motion, type Variants } from 'framer-motion';
import { Eye, EyeOff, Lock, Mail, Shield } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from './AuthContext';
import { TwoFactorSetup } from './components/TwoFactorSetup';
import { TwoFactorVerify } from './components/TwoFactorVerify';

const schema = yup.object({
  email: yup.string().email('Email inválido').required('Email é obrigatório'),
  password: yup.string().required('Senha é obrigatória'),
});

type LoginFormData = yup.InferType<typeof schema>;

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' as const },
  },
};

export function Login() {
  const { isAuthenticated, login, complete2FASetup, complete2FALogin } =
    useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginStep, setLoginStep] = useState<
    'login' | 'setup2fa' | 'verify2fa'
  >('login');
  const [tempToken, setTempToken] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  const from = (location.state as any)?.from?.pathname || '/';

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');

    try {
      const result = await login(data.email, data.password);

      if (result.requires2FASetup) {
        setTempToken(result.tempToken!);
        setLoginStep('setup2fa');
        return;
      }

      if (result.requiresTwoFactor) {
        setTempToken(result.tempToken!);
        setLoginStep('verify2fa');
        return;
      }

      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Erro ao fazer login');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASetupComplete = (user: any) => {
    complete2FASetup(user);
    navigate(from, { replace: true });
  };

  const handle2FAVerifyComplete = (user: any) => {
    complete2FALogin(user);
    navigate(from, { replace: true });
  };

  const handle2FAError = (errorMessage: string) => {
    setError(errorMessage);
    setLoginStep('login');
    setTempToken('');
  };

  if (loginStep === 'setup2fa') {
    return (
      <TwoFactorSetup
        tempToken={tempToken}
        onComplete={handle2FASetupComplete}
        onError={handle2FAError}
      />
    );
  }

  if (loginStep === 'verify2fa') {
    return (
      <TwoFactorVerify
        tempToken={tempToken}
        onComplete={handle2FAVerifyComplete}
        onError={handle2FAError}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[hsl(210,35%,32%)] to-[hsl(210,40%,25%)] px-4">
      <motion.div
        className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 md:p-10"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div variants={itemVariants} className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(210,35%,32%)] to-[hsl(210,40%,25%)] flex items-center justify-center shadow-lg">
              <img
                src="/favicon.png"
                alt="Planalto Capital"
                className="w-9 h-9 object-contain"
              />
            </div>
            <span className="text-xl font-bold text-[hsl(210,35%,32%)]">
              Planalto Capital
            </span>
          </div>
          <h1 className="text-2xl font-bold text-[hsl(210,50%,10%)] mb-2">
            Bem-vindo de volta!
          </h1>
          <p className="text-[hsl(215,16%,47%)] text-sm">
            Faça login para acessar seu dashboard
          </p>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-6"
          >
            <Alert variant="destructive" className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-700">
                {error}
              </AlertDescription>
            </Alert>
          </motion.div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <motion.div variants={itemVariants} className="space-y-2">
            <Label
              htmlFor="email"
              className="text-[hsl(210,50%,10%)] font-medium text-sm"
            >
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,16%,47%)]" />
              <Input
                id="email"
                type="email"
                placeholder="Digite seu email"
                {...register('email')}
                className={`pl-10 h-11 bg-[hsl(210,40%,98%)] border-[hsl(214,32%,91%)] text-[hsl(210,50%,10%)] placeholder:text-[hsl(215,16%,47%)] focus:border-[hsl(43,56%,61%)] focus:ring-[hsl(43,56%,61%)] transition-all ${
                  errors.email
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : ''
                }`}
              />
            </div>
            {errors.email && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm text-red-500"
              >
                {errors.email.message}
              </motion.p>
            )}
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-2">
            <Label
              htmlFor="password"
              className="text-[hsl(210,50%,10%)] font-medium text-sm"
            >
              Senha
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,16%,47%)]" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Digite sua senha"
                {...register('password')}
                className={`pl-10 pr-10 h-11 bg-[hsl(210,40%,98%)] border-[hsl(214,32%,91%)] text-[hsl(210,50%,10%)] placeholder:text-[hsl(215,16%,47%)] focus:border-[hsl(43,56%,61%)] focus:ring-[hsl(43,56%,61%)] transition-all ${
                  errors.password
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : ''
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(215,16%,47%)] hover:text-[hsl(210,35%,32%)] transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {errors.password && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm text-red-500"
              >
                {errors.password.message}
              </motion.p>
            )}
          </motion.div>

          <motion.div variants={itemVariants} className="pt-2">
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 bg-[hsl(210,35%,32%)] hover:bg-[hsl(210,35%,25%)] text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <motion.div
                    className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: 'linear',
                    }}
                  />
                  Entrando...
                </span>
              ) : (
                'Entrar'
              )}
            </Button>
          </motion.div>
        </form>

        <motion.div
          variants={itemVariants}
          className="mt-6 pt-6 border-t border-[hsl(214,32%,91%)]"
        >
          <div className="flex items-center justify-center gap-2">
            <Shield className="w-4 h-4 text-[hsl(43,56%,61%)]" />
            <span className="text-xs text-[hsl(215,16%,47%)]">
              Protegido com autenticação de dois fatores
            </span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
